import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useController } from 'react-hook-form';
import { Chip, TextField, Autocomplete } from '@mui/material';
import { businessKeywordsFirst, keywordKey, normalizeKeywords, splitKeywords } from './keywordValues';

const EMPTY_KEYWORDS = [];

export const KeywordInput = ({ control, options = EMPTY_KEYWORDS, popupZIndex, onPendingInputChange, disabled = false }) => {
  const { field } = useController({ name: 'keywords', control });
  const [inputValue, setInputValue] = useState('');
  const businessOptions = useMemo(() => normalizeKeywords(options), [options]);
  const businessKeys = useMemo(() => new Set(businessOptions.map(keywordKey)), [businessOptions]);
  const value = useMemo(() => businessKeywordsFirst(field.value, businessOptions), [field.value, businessOptions]);
  const available = businessOptions.filter((option) => !value.some((keyword) => keywordKey(keyword) === keywordKey(option)));

  const changeInput = (text) => {
    if (disabled) return;
    setInputValue(text);
    onPendingInputChange?.(text);
  };
  const changeKeywords = (keywords) => { if (!disabled) field.onChange(businessKeywordsFirst(keywords, businessOptions)); };
  const addKeywords = (text) => {
    changeKeywords([...value, ...splitKeywords(text)]);
    changeInput('');
  };

  const handleKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.which === 229) {
      event.defaultMuiPrevented = true;
      return;
    }
    const selectingOption = event.key === 'Enter' && event.target.getAttribute('aria-activedescendant');
    if (!selectingOption && (event.key === 'Enter' || event.key === ',')) {
      event.preventDefault();
      event.defaultMuiPrevented = true;
      if (inputValue.trim()) addKeywords(inputValue);
    }
  };

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData('text');
    if (!text) return;
    event.preventDefault();
    addKeywords(text);
  };

  return (
    <Autocomplete
      disabled={disabled}
      freeSolo
      multiple
      autoSelect
      options={available}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, text) => changeInput(text)}
      onChange={(_, keywords) => changeKeywords(keywords)}
      onKeyDown={handleKeyDown}
      onBlur={field.onBlur}
      componentsProps={{ popper: { sx: { zIndex: popupZIndex } } }}
      renderTags={(tags, getTagProps) => tags.map((keyword, index) => (
        <Chip {...getTagProps({ index })} key={keyword} label={keyword}
          color={businessKeys.has(keywordKey(keyword)) ? 'primary' : 'default'} />
      ))}
      renderInput={(params) => (
        <TextField {...params} fullWidth inputRef={field.ref} label="Keywords" variant="outlined"
          onPaste={handlePaste}
          helperText="Type a keyword and press Enter or comma, or choose a suggestion. Typed text is also added when you leave the field." />
      )}
    />
  );
};

const KeywordSelector = ({ control }) => {
  const businessKeywords = useSelector((state) => state.business.keywords);
  return <KeywordInput control={control} options={businessKeywords || EMPTY_KEYWORDS} />;
};

export default KeywordSelector;
